import type { Weekday } from '../types/planner';

export const WEEKDAYS: readonly Weekday[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const pad = (value: number): string => String(value).padStart(2, '0');

export function isoDay(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function dateOf(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
}

export function addDays(day: string, count: number): string {
  const date = dateOf(day);
  date.setDate(date.getDate() + count);
  return isoDay(date);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((dateOf(to).getTime() - dateOf(from).getTime()) / 86_400_000);
}

export function weekdayIndex(day: string): number {
  return dateOf(day).getDay();
}

export function weekdayOf(day: string): Weekday {
  return WEEKDAYS[weekdayIndex(day)];
}

export function weekStart(day: string, startsOn: Weekday): string {
  const offset = (weekdayIndex(day) - WEEKDAYS.indexOf(startsOn) + 7) % 7;
  return addDays(day, -offset);
}

export function rangeDays(from: string, count: number): string[] {
  return Array.from({ length: count }, (_, index) => addDays(from, index));
}

export function deviceToday(now: Date = new Date()): string {
  return isoDay(now);
}

export function minutesOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function localDayOf(moment: string): string {
  return isoDay(new Date(moment));
}

export function localMinuteOf(moment: string): number {
  return minutesOfDay(new Date(moment));
}

export function momentAt(day: string, minute: number): string {
  const date = dateOf(day);
  date.setHours(Math.floor(minute / 60), minute % 60, 0, 0);
  return date.toISOString();
}

export function clockTime(minute: number): string {
  return `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`;
}

export function parseClock(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

export function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function usesTwelveHourClock(locale: string): boolean {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12 === true;
}

export function formatClock(minute: number, locale: string): string {
  const date = new Date(2000, 0, 1, Math.floor(minute / 60), minute % 60);
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

export function formatShortDay(day: string, locale: string): string {
  return dateOf(day).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'numeric' });
}

export function formatLongDay(day: string, locale: string): string {
  return dateOf(day).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
}

export function formatDayMonth(day: string, locale: string): string {
  return dateOf(day).toLocaleDateString(locale, { day: 'numeric', month: 'numeric' });
}

export function weekdayLetter(day: string, locale: string): string {
  return dateOf(day).toLocaleDateString(locale, { weekday: 'narrow' });
}

export function weekdayName(weekday: Weekday, locale: string, width: 'long' | 'short' = 'long'): string {
  const sunday = new Date(2026, 9, 4);
  sunday.setDate(sunday.getDate() + WEEKDAYS.indexOf(weekday));
  return sunday.toLocaleDateString(locale, { weekday: width });
}

export function monthTitle(year: number, month: number, locale: string): string {
  return new Date(year, month, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

export function writesDayFirst(locale: string): boolean {
  const parts = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric' }).formatToParts(
    new Date(2026, 9, 7),
  );
  return parts.findIndex((part) => part.type === 'day') < parts.findIndex((part) => part.type === 'month');
}
